import React from 'react';
import { Box } from '@mui/material';

import { FormInput } from '@occc-app/Offer/components/FormInput';

import {
  greaterThanZeroValidation,
  numberValidation,
  requiredValidation,
  withFormEventValidation,
} from '../../validatableComponent';
import { State, PerGroupPurchasedGroup } from '../redux';
import { ButtonGroup } from '../../ButtonGroup';
import { FormTextarea } from '../../FormTextarea';

const CountThresholdFormInput = withFormEventValidation(
  FormInput,
  requiredValidation,
  numberValidation,
  greaterThanZeroValidation,
);

const ValuesFormTextarea = withFormEventValidation(FormTextarea, requiredValidation);

type ItemGroup = PerGroupPurchasedGroup & {
  createOfferMode: boolean;
  editMode: boolean;
  fieldDisabled: (field: Fields) => boolean;
};
const ItemGroup: React.FC<ItemGroup> = (props) => {
  const { countPer, countThreshold, fieldDisabled, itemKey, summationMode, values } = props;

  return (
    <Box>
      <Box mb={4}>
        <ButtonGroup
          label='Summation Mode'
          name='summationMode'
          readonly={fieldDisabled('summationMode')}
          selectedValue={summationMode}
        >
          <ButtonGroup.Option value='include'>Include</ButtonGroup.Option>
          <ButtonGroup.Option value='exclude'>Exclude</ButtonGroup.Option>
        </ButtonGroup>
      </Box>

      <Box mb={4}>
        <ButtonGroup
          label='Count Per'
          name='countPer'
          readonly={fieldDisabled('countPer')}
          selectedValue={countPer}
        >
          <ButtonGroup.Option value='quantity'>Quantity</ButtonGroup.Option>
          <ButtonGroup.Option value='money_amount'>Money Amount</ButtonGroup.Option>
        </ButtonGroup>
      </Box>

      <Box mb={4}>
        <CountThresholdFormInput
          id='countThreshold'
          label='Count Threshold'
          readonly={fieldDisabled('countThreshold')}
          type='text'
          value={countThreshold}
          required={true}
        />
      </Box>

      <Box mb={4}>
        <ButtonGroup
          label='Line item key'
          name='itemKey'
          readonly={fieldDisabled('itemKey')}
          selectedValue={itemKey}
        >
          <ButtonGroup.Option value='group'>Group</ButtonGroup.Option>
          <ButtonGroup.Option value='upc'>UPC</ButtonGroup.Option>
          <ButtonGroup.Option value='sku'>SKU</ButtonGroup.Option>
        </ButtonGroup>
      </Box>

      <Box mb={4}>
        <ValuesFormTextarea
          id='values'
          label='Values'
          readonly={fieldDisabled('values')}
          value={values.join(', ')}
          required={true}
        />
      </Box>
    </Box>
  );
};

type ComponentState = State['emConfiguration']['perGroupPurchased'];
type Fields = keyof ComponentState['groups'][0];
export type Props = ComponentState & {
  createOfferMode: boolean;
  editMode: boolean;
  fieldDisabled: (field: Fields) => boolean;
};

const PerGroupPurchasedConfiguration: React.FC<Props> = (props) => {
  const { createOfferMode, editMode, fieldDisabled, groups } = props;

  const renderGroup = (group: PerGroupPurchasedGroup, idx: number) => {
    return (
      <ItemGroup
        key={idx}
        createOfferMode={createOfferMode}
        editMode={editMode}
        fieldDisabled={fieldDisabled}
        {...group}
      />
    );
  };

  // State does not care about the order of these groups when adding/removing/updating
  // But the UI wants to preserve sorting order regardless of which was last updated
  const itemGroups = groups
    .filter((group) => !group.removed)
    .sort((groupA, groupB) => groupA.groupIndex - groupB.groupIndex)
    .map(renderGroup);
  return <Box>{itemGroups}</Box>;
};

export { PerGroupPurchasedConfiguration };
